import {addDays, fmtMDY, toISO} from '../lib/dateUtils.js';
import {calcBreedingTimeline, PIG_GROUPS} from '../lib/pig.js';
import type {PigBreedingCycle} from '../lib/pig.js';
import type {FarrowForm, FarrowingRecord} from './farrowingRecord.js';

export const farrowingLocationLabel = (value: string | null | undefined) =>
  ({
    'inside-hut': 'Inside Hut',
    'outside-hut': 'Outside Hut',
    'inside-pen': 'Inside Pen',
    'outside-pen': 'Outside Pen',
  })[value as 'inside-hut'] ||
  value ||
  '—';
export const farrowingQualityLabel = (value: string | null | undefined) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '—';

function cycleForDate(date: string, cycles: PigBreedingCycle[]) {
  if (!date) return null;
  const day = new Date(date + 'T12:00:00');
  return (
    cycles.find((cycle) => {
      const timeline = calcBreedingTimeline(cycle.exposureStart);
      return (
        timeline && day >= new Date(timeline.farrowingStart + 'T12:00:00') && day <= addDays(timeline.farrowingEnd, 14)
      );
    }) || null
  );
}

type Props = {
  form: FarrowForm;
  onChange: (next: FarrowForm) => void;
  cycles: PigBreedingCycle[];
  records: FarrowingRecord[];
  recordId?: string | null;
  resolveSire: (record: FarrowingRecord) => string | null | undefined;
};

export default function FarrowingRecordForm({form, onChange, cycles, records, recordId, resolveSire}: Props) {
  const matchedCycle = cycleForDate(form.farrowingDate, cycles);
  const timeline = matchedCycle ? calcBreedingTimeline(matchedCycle.exposureStart) : null;
  const recordedTags = new Set(
    records
      .filter((record) => {
        if (!timeline || !record.farrowingDate || record.id === recordId) return false;
        const day = new Date(record.farrowingDate + 'T12:00:00');
        return day >= new Date(timeline.farrowingStart + 'T12:00:00') && day <= addDays(timeline.farrowingEnd, 14);
      })
      .map((record) => String(record.sow ?? '').trim()),
  );
  const sowOptions = matchedCycle
    ? [...(matchedCycle.boar1Tags || '').split(/[\n,]+/), ...(matchedCycle.boar2Tags || '').split(/[\n,]+/)]
        .map((tag) => tag.trim())
        .filter((tag) => tag && !recordedTags.has(tag))
        .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
    : [];
  // A historical/manual sow remains visible even if today's cycle tags differ.
  if (sowOptions.length && form.sow && !sowOptions.includes(form.sow)) sowOptions.push(form.sow);
  const change = (key: keyof FarrowForm, value: string) => onChange({...form, [key]: value});
  const sire = resolveSire(form);
  return (
    <div className="farrowing-fields">
      <label>
        Farrowing Date
        <input
          type="date"
          data-farrow-date="1"
          value={form.farrowingDate}
          onChange={(event) => {
            const farrowingDate = event.target.value;
            const cycle = cycleForDate(farrowingDate, cycles);
            onChange({
              ...form,
              farrowingDate,
              ...(cycle ? {group: String(cycle.group), exposureStart: cycle.exposureStart || ''} : {}),
            });
          }}
        />
      </label>
      <label>
        Sow #
        {sowOptions.length ? (
          <select data-farrow-sow="1" value={form.sow} onChange={(event) => change('sow', event.target.value)}>
            <option value="">Choose…</option>
            {sowOptions.map((tag) => (
              <option key={tag} value={tag}>
                #{tag}
              </option>
            ))}
          </select>
        ) : (
          <input
            data-farrow-sow="1"
            value={form.sow}
            onChange={(event) => change('sow', event.target.value)}
            placeholder="e.g. 5"
          />
        )}
      </label>
      <label>
        Group{matchedCycle ? ' (from cycle)' : ''}
        {matchedCycle ? (
          <span className="farrowing-readonly">Group {matchedCycle.group}</span>
        ) : (
          <select value={form.group} onChange={(event) => change('group', event.target.value)}>
            {PIG_GROUPS.map((group) => (
              <option key={group} value={group}>
                Group {group}
              </option>
            ))}
          </select>
        )}
      </label>
      <label>
        Impregnation Date (auto)
        <span className="farrowing-readonly">
          {form.farrowingDate ? fmtMDY(toISO(addDays(form.farrowingDate, -116))) : '—'}
        </span>
        <small>116 days before farrowing date</small>
      </label>
      <label>
        Sire (from cycle)<span className="farrowing-readonly">{sire || 'Select sow and farrowing date'}</span>
      </label>
      <label>
        Exposure Start{matchedCycle ? ' (from cycle)' : ''}
        {matchedCycle ? (
          <span className="farrowing-readonly">{fmtMDY(matchedCycle.exposureStart)}</span>
        ) : (
          <input
            type="date"
            value={form.exposureStart}
            onChange={(event) => change('exposureStart', event.target.value)}
          />
        )}
      </label>
      <label>
        Total Born
        <input
          type="number"
          min="0"
          data-farrow-total-born="1"
          value={form.totalBorn}
          onChange={(event) => change('totalBorn', event.target.value)}
        />
      </label>
      <label>
        Deaths
        <input
          type="number"
          min="0"
          data-farrow-deaths="1"
          value={form.deaths}
          onChange={(event) => change('deaths', event.target.value)}
        />
        <small>
          Alive: {Math.max(0, (parseInt(String(form.totalBorn)) || 0) - (parseInt(String(form.deaths)) || 0))}
        </small>
      </label>
      <label>
        Mothering Quality
        <select value={form.motheringQuality} onChange={(event) => change('motheringQuality', event.target.value)}>
          {['', 'excellent', 'average', 'poor'].map((quality) => (
            <option key={quality} value={quality}>
              {farrowingQualityLabel(quality)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Location
        <select value={form.location} onChange={(event) => change('location', event.target.value)}>
          {['', 'inside-hut', 'outside-hut', 'inside-pen', 'outside-pen'].map((location) => (
            <option key={location} value={location}>
              {farrowingLocationLabel(location)}
            </option>
          ))}
        </select>
      </label>
      <label className="farrowing-field-wide">
        Demeanor
        <input
          value={form.demeanor}
          onChange={(event) => change('demeanor', event.target.value)}
          placeholder="e.g. Friendly, protective..."
        />
      </label>
      <label className="farrowing-field-wide">
        What Went Well?
        <textarea rows={3} value={form.wentWell} onChange={(event) => change('wentWell', event.target.value)} />
      </label>
      <label className="farrowing-field-wide">
        What Didn't Go Well?
        <textarea rows={3} value={form.didntGoWell} onChange={(event) => change('didntGoWell', event.target.value)} />
      </label>
      <label className="farrowing-field-wide">
        Deaths / Defects
        <textarea rows={3} value={form.defects} onChange={(event) => change('defects', event.target.value)} />
      </label>
    </div>
  );
}
